import React from "react";
import styled from "styled-components";
import LazyLoad from "react-lazyload";
import { Helmet } from "react-helmet";
import CTASection from "../../components/CTASection";
import tempImage from "../../assets/images/volunteer/leader.webp";

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;

  @media (max-width: 768px) {
    padding: 15px;
  }

  @media (max-width: 480px) {
    padding: 10px;
  }
`;

const Title = styled.h1`
  text-align: center;
  margin-bottom: 20px;
  font-size: 2.5em;

  @media (max-width: 768px) {
    font-size: 2em;
  }

  @media (max-width: 480px) {
    font-size: 1.8em;
  }
`;

const Image = styled.img`
  width: 100%;
  height: auto;
  margin-bottom: 20px;
`;

const Paragraph = styled.p`
  font-size: 1.1em;
  line-height: 1.6;
  margin-bottom: 20px;

  @media (max-width: 768px) {
    font-size: 1em;
    line-height: 1.5;
  }

  @media (max-width: 480px) {
    font-size: 0.9em;
    line-height: 1.4;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 20px;
  font-size: 1em;

  @media (max-width: 768px) {
    font-size: 0.9em;
  }

  @media (max-width: 480px) {
    font-size: 0.8em;
  }
`;

const TableRow = styled.tr`
  border-bottom: 1px solid #ddd;
`;

const TableHeader = styled.th`
  text-align: left;
  padding: 8px;
  background-color: var(--hhb-orange);
  color: white;

  @media (max-width: 768px) {
    padding: 6px;
  }

  @media (max-width: 480px) {
    padding: 5px;
  }
`;

const TableData = styled.td`
  padding: 8px;
  background-color: #fff;
  border: 1px solid #ddd;

  @media (max-width: 768px) {
    padding: 6px;
  }

  @media (max-width: 480px) {
    padding: 5px;
  }
`;

const SubTitle = styled.h2`
  font-size: var(--font-size-heading);
  margin-top: 40px;
  margin-bottom: 20px;

  @media (max-width: 768px) {
    font-size: 1.5em;
    margin-top: 30px;
    margin-bottom: 15px;
  }

  @media (max-width: 480px) {
    font-size: 1.3em;
    margin-top: 20px;
    margin-bottom: 10px;
  }
`;

const BecomeAContractor = () => {
  return (
    <>
      <Helmet>
        <title>Become A Contractor - Habitat for Humanity Buffalo</title>
        <meta
          name="description"
          content="Partner with Habitat for Humanity Buffalo as a contractor to help us build stronger communities and provide affordable housing. Learn about our requirements and how you can contribute."
        />
      </Helmet>
      <Container>
        <Title>Become A Habitat Buffalo Contractor</Title>
        <LazyLoad height={300} offset={100}>
          <Image src={tempImage} alt="Habitat for Humanity Buffalo" />
        </LazyLoad>
        <SubTitle className="section-header primary">
          Help Us Achieve Our Mission!
        </SubTitle>
        <Paragraph>
          Habitat for Humanity Buffalo is on a continuous quest to partner with
          skilled, dedicated contractors who share our vision of building a
          better community. Our mission thrives on collaboration, and together,
          we can create significant impact. Whether you're a seasoned
          professional or a rising talent in the construction industry, your
          expertise can play a pivotal role in transforming lives and landscapes
          across Western New York.
        </Paragraph>
        <Paragraph>
          Contractors interested in bidding on Habitat projects must be
          licensed, bonded, insured, and in good standing with the Contractor’s
          State License Board (“CSLB”).
        </Paragraph>
        <Paragraph>
          If you are interested in working with Habitat, please fill out the
          Contractor Interest Form below.
        </Paragraph>
        <SubTitle>Insurance Requirements:</SubTitle>
        <Table>
          <thead>
            <TableRow>
              <TableHeader>Requirement</TableHeader>
              <TableHeader>Details</TableHeader>
            </TableRow>
          </thead>
          <tbody>
            <TableRow>
              <TableData>Commercial General Liability</TableData>
              <TableData>
                Including bodily injury and property damage liability,
                independent Subcontractors liability, contractual liability,
                product liability, and completed operations liability.
                <ul>
                  <li>$1,000,000 per Occurrence</li>
                  <li>$2,000,000 Aggregate</li>
                </ul>
              </TableData>
            </TableRow>
            <TableRow>
              <TableData>Additional Insured</TableData>
              <TableData>
                Habitat for Humanity Buffalo, including its affiliated
                companies, successors, assigns, officers, directors, agents,
                servants, and employees are added as additional insured.
              </TableData>
            </TableRow>
            <TableRow>
              <TableData>Excess/Umbrella Coverage</TableData>
              <TableData>
                In excess of underlying coverage:
                <ul>
                  <li>$1,000,000 per occurrence</li>
                  <li>
                    $1,000,000 aggregate ($5,000,000 if doing asbestos work)
                  </li>
                </ul>
              </TableData>
            </TableRow>
            <TableRow>
              <TableData>Automobile Liability</TableData>
              <TableData>
                Per occurrence for bodily injury and property damage including
                owned, hired, and non-owned vehicle coverage.
                <ul>
                  <li>Combined Single Limit no less than $1,000,000</li>
                </ul>
              </TableData>
            </TableRow>
            <TableRow>
              <TableData>Workers Compensation Insurance Requirements</TableData>
              <TableData>
                <ul>
                  <li>
                    Limits of not less than $1,000,000 each accident for bodily
                    injury by accident
                  </li>
                  <li>$1,000,000 each employee for bodily injury by disease</li>
                  <li>$1,000,000 policy limit for bodily injury by disease</li>
                </ul>
              </TableData>
            </TableRow>
          </tbody>
        </Table>
        <CTASection
          title="Contractor Interest Form"
          description="If you are interested in working with Habitat, please fill out the Contractor Interest Form below."
          buttonText="Fill Out Form"
          buttonLink="/form/contractor"
        />
      </Container>
    </>
  );
};

export default BecomeAContractor;
